import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UseExpandedRowsReturn {
  expandedRows: Set<number>;
  toggleRow: (id: number) => void;
  isExpanded: (id: number) => boolean;
}

export function useExpandedRows(paramName: string = 'expanded'): UseExpandedRowsReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);

  const getInitialExpanded = (): Set<number> => {
    const param = searchParams.get(paramName);
    if (!param) return new Set();

    return new Set(
      param
        .split(',')
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id) && id > 0)
    );
  };

  const [expandedRows, setExpandedRows] = useState<Set<number>>(getInitialExpanded);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = new URLSearchParams(searchParams);

    if (expandedRows.size > 0) {
      const sortedIds = Array.from(expandedRows).sort((a, b) => a - b);
      params.set(paramName, sortedIds.join(','));
    } else {
      params.delete(paramName);
    }

    setSearchParams(params, { replace: true });
  }, [expandedRows, paramName, setSearchParams]);

  const toggleRow = useCallback((id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback((id: number) => expandedRows.has(id), [expandedRows]);

  return { expandedRows, toggleRow, isExpanded };
}
